export type AttributionPayload = Record<string, string>;

let pendingAttribution: AttributionPayload | null = null;

// Security: Limit size of attribution values to prevent bloating Auth0 appState
// and backend payloads with attacker-controlled query parameters
const MAX_PARAM_VALUE_LENGTH = 200;
const MAX_INITIAL_PATH_LENGTH = 500;
const META_COOKIE_FBC = "_fbc";
const META_COOKIE_FBP = "_fbp";

const PARAM_KEYS = [
  "ad_id",
  "adset_id",
  "campaign_id",
  "fbclid",
  "gclid",
  "placement",
  "utm_campaign",
  "utm_content",
  "utm_medium",
  "utm_source",
  "utm_term",
];

const truncateValue = (value: string, maxLength: number) => {
  const trimmedValue = value.trim();
  return trimmedValue.length > maxLength ? trimmedValue.substring(0, maxLength) : trimmedValue;
};

const getCookieValue = (name: string): string | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }
  const cookies = document.cookie.split(";").map((cookie) => cookie.trim());
  const match = cookies.find((cookie) => cookie.startsWith(`${name}=`));
  if (!match) {
    return undefined;
  }
  const value = match.substring(name.length + 1);
  return value ? decodeURIComponent(value) : undefined;
};

const getFbpFromCookie = () => {
  const existing = getCookieValue(META_COOKIE_FBP);
  if (existing && existing.trim()) {
    return truncateValue(existing, MAX_PARAM_VALUE_LENGTH);
  }
  return undefined;
};

const getFbcFromCookie = () => {
  const existing = getCookieValue(META_COOKIE_FBC);
  if (existing && existing.trim()) {
    return truncateValue(existing, MAX_PARAM_VALUE_LENGTH);
  }
  return undefined;
};

const buildFbc = (fbclid: string) => {
  const timestampSeconds = Math.floor(Date.now() / 1000);
  const value = `fb.1.${timestampSeconds}.${fbclid}`;
  return truncateValue(value, MAX_PARAM_VALUE_LENGTH);
};

export const buildAttributionFromLocation = (
  loc: Location = window.location,
): AttributionPayload | undefined => {
  const searchParams = new URLSearchParams(loc.search);
  const attribution: AttributionPayload = {};

  PARAM_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value && value.trim()) {
      // Truncate value to prevent bloating appState with attacker-controlled data
      attribution[key] = truncateValue(value, MAX_PARAM_VALUE_LENGTH);
    }
  });

  if (Object.keys(attribution).length) {
    const fbp = getFbpFromCookie();
    if (fbp) {
      attribution.fbp = fbp;
    }
    const fbc = getFbcFromCookie();
    if (fbc) {
      attribution.fbc = fbc;
    } else if (attribution.fbclid) {
      attribution.fbc = buildFbc(attribution.fbclid);
    }
    // Truncate pathname to prevent bloating appState
    attribution.initial_path =
      loc.pathname.length > MAX_INITIAL_PATH_LENGTH
        ? loc.pathname.substring(0, MAX_INITIAL_PATH_LENGTH)
        : loc.pathname;
    attribution.ts = new Date().toISOString();
    return attribution;
  }
  return undefined;
};

export const setPendingAttribution = (attribution?: AttributionPayload) => {
  pendingAttribution = attribution || null;
};

export const getPendingAttribution = (): AttributionPayload | null => {
  const current = pendingAttribution;
  pendingAttribution = null;
  return current;
};
